import { useMemo, useState } from 'react';

import { ColumnsType } from 'antd/es/table';

import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';

import TitleBar from '../../../../components/TitleBar';
import {
  AntModal,
  BorderedSection,
  Button,
  CommentBox,
  Loading,
} from '../../../../components';

import ExposureSummaryTable from './ExposureSummaryTable';
import StrategyComponents from './StrategyComponents';
import {
  FETCH_PROCESSES_AREAS_WITH_STRATEGY_BY_LOCATION,
  FETCH_PROCESSES_MITIGATION_OPTIONS,
  FETCH_PROCESS_AREA_MITIGATION_OPTIONS,
} from '../../../dataInputs/queries/processAreas';
import { findObjectByKey } from './utils';
import {
  lastUpdateDateFormatter,
  tableSortingMethods,
} from '../../../../utils';
import { UPDATE_STRATEGY } from '../../queries';
import { FinalizeStrategy } from '../forms/FinalizeStrategy';
import useBCPMetaData from '../../../../hooks/useBCPMetaData';
import TableV3 from '../../../../components/generic/Table/TableV3';
import { SummaryContent } from '../components';
import StrategyStatus from '../components/StrategyStatus';
import FinancialData from '../../../../components/common/FinancialData';
import QueryErrorBoundary from '../../../../components/error-handling';
import { useUserContext } from '../../../../context/PermissionsContext';
import { baseColumns } from './exportColumns';
import TitleUpdater from '../../../../components/common/TitleUpdater';

const normalizedData = (data: any) => {
  const formatedData = data?.map((item: any) => {
    const { productGroups, ...rest } = item;
    return {
      key: item.uuid,
      ...rest,
      ...(item.productGroups?.length > 0
        ? {
            children: item.productGroups?.map((productGroup: any) => {
              const { products, ...rest } = productGroup;
              return {
                key: productGroup.uuid,
                ...rest,
                ...(productGroup.products?.length > 0
                  ? {
                      children: productGroup.products?.map((product: any) => {
                        return {
                          key: product.uuid,
                          ...product,
                        };
                      }),
                    }
                  : {}),
              };
            }),
          }
        : {}),
    };
  });

  return formatedData;
};

const StrategyReview = () => {
  const [openFinalizeStrategyModal, setOpenFinalizeStrategyModal] =
    useState(false);
  const navigate = useNavigate();
  const { locationId, processAreaId } = useParams();

  const { isAdmin, canEdit, canViewFinancials, canApprove } = useUserContext();

  const {
    locationName,
    loading: metaDataLoading,
    error: metaDataError,
  } = useBCPMetaData(locationId!);

  const { data, loading, error } = useQuery(
    FETCH_PROCESSES_AREAS_WITH_STRATEGY_BY_LOCATION,
    {
      variables: { uuid: locationId },
      skip: !locationId,
    },
  );

  const [UpdateStrategySummary] = useMutation(UPDATE_STRATEGY, {
    refetchQueries: ['FindAllProcessAreasByLocation'],
  });

  const { processAreaData, IS_PROCESS_AREA, MITIGATION_TYPE } = useMemo(() => {
    const processArea = data?.findAll
      ? findObjectByKey(data.findAll, processAreaId ?? '')
      : null;

    return {
      processAreaData: processArea,
      IS_PROCESS_AREA: processArea?.__typename === 'ProcessArea',
      MITIGATION_TYPE:
        processArea?.__typename === 'ProcessArea'
          ? 'process-area-mitigation'
          : 'process-mitigation',
    };
  }, [data, processAreaId]);

  const query = IS_PROCESS_AREA
    ? FETCH_PROCESS_AREA_MITIGATION_OPTIONS
    : FETCH_PROCESSES_MITIGATION_OPTIONS;

  const { data: mitigationData, loading: mitigationLoading } = useQuery(query, {
    variables: { uuid: processAreaId },
    skip: !processAreaId,
  });

  const loadingStates = metaDataLoading || loading || mitigationLoading;

  const formattedData = useMemo(
    () =>
      normalizedData(
        IS_PROCESS_AREA
          ? mitigationData?.processAreaMitigationOptions ?? []
          : mitigationData?.processMitigationOptions ?? [],
      ),
    [mitigationData, IS_PROCESS_AREA],
  );

  const customColumns: ColumnsType<any> = [
    {
      key: 'name',
      title: '',
      width: 150,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.name, b.name),
      render: ({ name }) => (
        <span className="custom-table--column font-bold">{name}</span>
      ),
    },
    {
      key: 'finishedGoodsInventory',
      title: 'Finished Goods Inventory',
      width: 100,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) =>
            tableSortingMethods.number(a.totalInventory, b.totalInventory)
        : undefined,
      render: ({ totalInventory }) => (
        <span className="custom-table--column">
          {<FinancialData text={totalInventory} />}
        </span>
      ),
    },
    {
      key: 'onsite',
      title: 'Onsite',
      width: 100,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.totalOnsite, b.totalOnsite)
        : undefined,
      render: ({ totalOnsite }) => (
        <span className="custom-table--column">
          {<FinancialData text={totalOnsite} />}
        </span>
      ),
    },
    {
      key: 'internal',
      title: 'Internal',
      width: 100,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.totalInternal, b.totalInternal)
        : undefined,
      render: ({ totalInternal }) => (
        <span className="custom-table--column">
          {<FinancialData text={totalInternal} />}
        </span>
      ),
    },
    {
      key: 'external',
      title: 'External',
      width: 100,
      align: 'right',
      sorter: canViewFinancials
        ? (a, b) => tableSortingMethods.number(a.totalExternal, b.totalExternal)
        : undefined,
      render: ({ totalExternal }) => (
        <span className="custom-table--column">
          {<FinancialData text={totalExternal} />}
        </span>
      ),
    },
    {
      key: '',
      title: '',
      width: 200,
      align: 'left',
      render: (_, record) => (
        <section className="custom-table--column flex items-center gap-2">
          <section className="w-full relative">
            <CommentBox
              text={record?.strategyComment}
              placeholder="No comment provided"
            />
          </section>
        </section>
      ),
    },
  ];

  const extendSectionOptions = (isAdmin || canEdit) &&
    processAreaData?.strategy?.required && (
      <Button
        type="primary"
        className="min-w-[160px] text-sm"
        onClick={() =>
          navigate(
            `/mitigation/${locationId}/${MITIGATION_TYPE}/${processAreaId}?location=${locationName}&processArea=${processAreaData?.name}`,
          )
        }
      >
        Edit Mitigation
      </Button>
    );

  if (metaDataLoading || loading) {
    return <Loading />;
  }

  return (
    <QueryErrorBoundary error={metaDataError}>
      <TitleUpdater
        title={`${processAreaData?.name} / ${
          processAreaData?.replacementTime || 0
        } ${
          processAreaData?.replacementTime > 1 ? 'Months' : 'Month'
        } Strategy`}
      />
      <TitleBar
        title="BCP Hub"
        subtitle={`${locationName} //  ${processAreaData?.name} // ${
          processAreaData?.replacementTime || 0
        } ${
          processAreaData?.replacementTime > 1 ? 'Months' : 'Month'
        } Strategy`}
      />

      <QueryErrorBoundary error={error}>
        <section className="grid gap-6">
          <StrategyStatus
            status={processAreaData?.strategy?.bcpStatus}
            lastUpdate={processAreaData?.strategy?.finalizedAt}
          />
          <SummaryContent
            title="Mitigation Strategy Commentary"
            data={processAreaData?.strategy}
            updateGQL={UpdateStrategySummary}
            loading={loading}
          />
          <BorderedSection
            title="Mitigation Options"
            noPadding
            extendOptions={extendSectionOptions}
          >
            <TableV3
              data={formattedData}
              columns={customColumns}
              loading={loadingStates}
              useBorderedSection={false}
              customStyles="nested-columns--custom"
              exportExcelColumns={baseColumns}
            />
          </BorderedSection>
          <section className="grid lg:grid-cols-2 gap-6">
            <ExposureSummaryTable data={processAreaData?.strategy} />
            <StrategyComponents
              strategyID={processAreaData?.strategy?.uuid}
              locationName={locationName}
              processArea={processAreaData?.name}
              replacementTime={processAreaData?.replacementTime}
            />
          </section>
          {canApprove && (
            <section className="w-full flex justify-end mb-4">
              <Button
                type="primary"
                className="w-[250px] text-sm mb-4"
                onClick={() => setOpenFinalizeStrategyModal(true)}
              >
                Finalize Strategy
              </Button>
            </section>
          )}
        </section>
      </QueryErrorBoundary>
      <Button
        type="primary"
        className="w-[350px] text-sm mb-4"
        onClick={() =>
          navigate(`/process-areas-and-strategies/location/${locationId}`)
        }
      >
        Back to Strategy Inventory
      </Button>
      <AntModal
        title="Finalize Strategy"
        openModal={openFinalizeStrategyModal}
        onCancel={() => setOpenFinalizeStrategyModal(false)}
      >
        <FinalizeStrategy
          lastUpdate={
            processAreaData?.strategy?.lastUpdate &&
            processAreaData?.strategy?.bcpStatus?.toUpperCase() === 'FINALIZED'
              ? lastUpdateDateFormatter(processAreaData?.strategy?.lastUpdate)
              : 'NEVER'
          }
          strategyId={processAreaData?.strategy?.uuid}
          onCancel={() => setOpenFinalizeStrategyModal(false)}
        />
      </AntModal>
    </QueryErrorBoundary>
  );
};

export default StrategyReview;
